import React, { useState } from "react";
import {
  AutoAwesome,
  Campaign,
  CheckCircle,
  Contacts,
  DynamicFeed,
  Info,
  Schedule,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import axios from "axios";
import TemplatePreview from "../../widgets/TemplatePreview";
import { API_ENDPOINT } from "../../utils/constants";
import { getErrorResponse, getHeader } from "../../utils/util";
import { useDialog } from "../../context/DialogContext";
import SplitsDisplay from "./contacts/SplitsDisplay";

const SummaryItem = ({ icon, label, children }) => (
  <Box
    sx={{
      height: "100%",
      p: 2,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      bgcolor: "grey.50",
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
      <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
    </Stack>
    <Box sx={{ color: "text.primary", fontWeight: 600 }}>{children}</Box>
  </Box>
);

const formatCampaignDate = (value) => {
  if (!value) return "Not selected";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Invalid schedule";

  return format(date, "dd MMM yyyy, hh:mm a");
};

const RdCampaignStep3 = ({ campaignPost, setCampaignPost }) => {
  const { showDialog } = useDialog();
  const [splitLoading, setSplitLoading] = useState(false);

  const totalContacts =
    Number(campaignPost.contactsCount) || campaignPost.contacts?.length || 0;
  const splitsData = Array.isArray(campaignPost.dataSplitsList)
    ? campaignPost.dataSplitsList
    : [];

  const handleSplitSubmit = async () => {
    if (totalContacts < 2) return;

    try {
      setSplitLoading(true);
      const response = await axios.post(
        `${API_ENDPOINT}/campaign/split-campaign`,
        { count: totalContacts },
        getHeader()
      );
      const generatedSplits = response?.data?.splits || [];

      if (generatedSplits.length === 0) {
        showDialog(
          "No delivery plan generated",
          "We could not create campaign batches for this audience. You can still send the campaign normally.",
          { type: "info", onConfirm: () => {} }
        );
        return;
      }

      setCampaignPost((previous) => ({
        ...previous,
        dataSplitsList: generatedSplits,
      }));
    } catch (error) {
      showDialog(
        error?.response?.data?.title || "Unable to create delivery plan",
        getErrorResponse(error),
        { type: "error", onConfirm: () => {} }
      );
    } finally {
      setSplitLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 920, mx: "auto" }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2.5,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              display: "grid",
              placeItems: "center",
              borderRadius: 2,
              bgcolor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            <Campaign fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Review your campaign
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Check the audience, template, and delivery settings before
              sending.
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <SummaryItem icon={<Info fontSize="small" />} label="Campaign name">
              <Typography variant="body2" fontWeight={700}>
                {campaignPost.campaignName || "Unnamed campaign"}
              </Typography>
            </SummaryItem>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SummaryItem
              icon={<CheckCircle fontSize="small" />}
              label="Template"
            >
              <Typography variant="body2" fontWeight={700} noWrap>
                {campaignPost.template?.name || "No template selected"}
              </Typography>
            </SummaryItem>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SummaryItem
              icon={<DynamicFeed fontSize="small" />}
              label="Parameters"
            >
              <Chip
                label={campaignPost.isStaticParams ? "Static" : "Dynamic"}
                size="small"
                color={campaignPost.isStaticParams ? "default" : "primary"}
                variant="outlined"
              />
            </SummaryItem>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SummaryItem
              icon={<Schedule fontSize="small" />}
              label="Delivery"
            >
              <Typography variant="body2" fontWeight={700}>
                {campaignPost.isCampaignScheduled
                  ? formatCampaignDate(campaignPost.scheduledDate)
                  : "Send immediately"}
              </Typography>
            </SummaryItem>
          </Grid>
          <Grid item xs={12}>
            <SummaryItem
              icon={<Contacts fontSize="small" />}
              label="Target audience"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight={700}>
                  {totalContacts.toLocaleString("en-IN")} contacts
                </Typography>
                {campaignPost.isMarketing && (
                  <Chip
                    label="Database audience"
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Stack>
            </SummaryItem>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
            Message preview
          </Typography>
          <Box
            sx={{
              p: { xs: 1, sm: 2 },
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <TemplatePreview
              template={campaignPost.template}
              previewUrl={campaignPost.header?.mediaUrl ?? ""}
            />
          </Box>
        </Box>

        {totalContacts > 1 && splitsData.length === 0 && (
          <Alert
            severity="info"
            variant="outlined"
            sx={{ mt: 3, alignItems: "center", borderRadius: 2 }}
            action={
              <Button
                size="small"
                onClick={handleSplitSubmit}
                disabled={splitLoading}
                startIcon={
                  splitLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <AutoAwesome fontSize="small" />
                  )
                }
              >
                {splitLoading ? "Creating…" : "Create plan"}
              </Button>
            }
          >
            <Typography variant="body2" fontWeight={700}>
              Optional: optimize delivery with AI
            </Typography>
            <Typography variant="caption">
              Create a suggested batch schedule for this audience, or continue
              without one.
            </Typography>
          </Alert>
        )}

        {splitsData.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <SplitsDisplay splitsData={splitsData} />
            <Button
              size="small"
              color="inherit"
              onClick={() =>
                setCampaignPost((previous) => {
                  const campaign = { ...previous };
                  delete campaign.dataSplitsList;
                  return campaign;
                })
              }
              sx={{ mt: 1, color: "text.secondary" }}
            >
              Remove delivery plan
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default RdCampaignStep3;
